import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import HeroSection from "../../components/WorkBag/HeroSection.tsx";
import StickyNavbar from "../../components/WorkBag/StickyNavbar.tsx";
import WorkBagForm from "./WorkBagForm";
import WorkOfferGrid from "../../components/WorkBag/WorkOfferGrid.tsx";
import useGetTodayDate from "../../hooks/useGetTodayDate";
import { useScrollDetection } from "../../hooks/useScrollDetection.ts";
import { getSkills, createWorkBagUser, openIdentityModal, getAllWorkOffers } from "../../store/actions";
import { fireAnalytics } from "../../utils/firebase";
import Logo from "../../assets/images/logo-navbar.png";
import { getSessionId, getDeviceCategory, getPreviousPage } from "../../utils/analyticsHelpers.ts";
import styles from "./WorkBagNew.module.scss";
import { 
  WorkBagProps, 
  WorkBagFormValues, 
  TabType,
  RootState 
} from "../../types/workBag";

const WorkBagFormTyped = WorkBagForm as any;

const WorkBag: React.FC<WorkBagProps> = React.memo(({ 
  getSkills, 
  skills, 
  createWorkBagUser, 
  allJobRequests,
  getAllWorkOffers
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("form");
  const [isPhone, setIsPhone] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  
  const recaptchaRef = useRef<any>(null);
  const tabContentRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const { isScrolled, scrollY } = useScrollDetection(100);


  
  const today = useGetTodayDate();
  const navigate = useNavigate();

  const handleResize = useCallback(() => {
    const windowWidth = window.innerWidth;
    const wasPhone = isPhone;
    const newIsPhone = windowWidth <= 900;
    setIsPhone(newIsPhone); 
    

    if (wasPhone !== newIsPhone) {
      setIsCompact(false);
    }
  }, [isPhone]);

  useEffect(() => {
    getSkills();
    getAllWorkOffers();
    
    
    // Nuevo evento estandarizado
    fireAnalytics("page_work_bag_view", {
      session_id: getSessionId(),
      source: getPreviousPage() ? "navigation" : "direct",
      previous_page: getPreviousPage(),
      device_category: getDeviceCategory(),
      category: "navigation",
      action: "page_view",
      page_name: "work_bag"
    });
    
    handleResize();
  }, [getSkills, getAllWorkOffers, handleResize]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const initialValues: WorkBagFormValues = {
    name: "",
    lastname: "",
    email: "",
    phone: "",
    skills: [],
    workZone: [],
    city: "",
    unemploymentFund: false,
    currentJob: false,
    employmentEndDate: "",
    termsAndConditions: false,
    selectedJurisdiction: null,
  };

  const telRegex = /[0-9+]/;

  const formSchema = Yup.object({
    name: Yup.string().required("Nombre*"),
    lastname: Yup.string().required("Apellido*"),
    email: Yup.string().email("Email invalido").required("Email*"),
    phone: Yup.string()
      .matches(telRegex, "*Solo se permiten números")
      .min(6, "*Número no válido")
      .required("Teléfono*"),
    skills: Yup.array().required("Debes seleccionar al menos un rubro de trabajo*"),
    workZone: Yup.array().required("Elige al menos una provincia*"),
    unemploymentFund: Yup.boolean().required("*Campo obligatorio"),
    currentJob: Yup.boolean().required("*Campo obligatorio"),
    employmentEndDate: Yup.string().when("currentJob", {
      is: true,
      then: Yup.string().required("*Debes seleccionar una fecha aprox."),
      otherwise: Yup.string(),
    }),
    selectedJurisdiction: Yup.object().nullable(),
    termsAndConditions: Yup.boolean().oneOf([true], "*Debes aceptar los términos y condiciones"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: formSchema,
    onSubmit: async (values) => {
      try {
        if (!recaptchaRef.current) {
          throw new Error("reCAPTCHA no está disponible");
        }

        const tokenRecaptcha = await recaptchaRef.current.executeAsync();

        if (!tokenRecaptcha) {
          throw new Error("No se recibió el token de reCAPTCHA");
        }

        const finalValues = {
          ...values,
          tokenRecaptcha,
        };

        createWorkBagUser(finalValues, navigate);
        formik.setErrors({});
        formik.resetForm();
      } catch (error) {
        console.error("Error al enviar el formulario:", error);
        alert("Ocurrió un error al validar el formulario. Por favor intentá de nuevo.");
      }
    },
  });

  const handleStickyNavbarTabChange = (tab: TabType) => {
    setActiveTab(tab);
    
    if (tab === "offers") {
      
      // Nuevo evento estandarizado
      fireAnalytics("page_work_offers_view", {
        session_id: getSessionId(),
        source: "navigation",
        previous_page: "/bolsa-trabajo",
        device_category: getDeviceCategory(),
        category: "navigation",
        action: "page_view",
        page_name: "work_offers"
      });
    }
  };


  const handleHeroSectionTabChange = (tab: TabType) => {
    setActiveTab(tab);
    

    if (isPhone) {
      setIsCompact(true);
    }
    

    if (!isPhone && tabContentRef.current) {
      setTimeout(() => {
        const tabContent = tabContentRef.current;
        
        if (tabContent) {
          const tabContentRect = tabContent.getBoundingClientRect();
          const currentScrollY = window.scrollY;
          const tabContentTop = currentScrollY + tabContentRect.top;
          

          const offset = tab === "form" ? 300 : 200;
          const targetScrollY = tabContentTop - offset;
          
          window.scrollTo({
            top: targetScrollY,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    
    if (tab === "offers") {
      
      // Nuevo evento estandarizado
      fireAnalytics("page_work_offers_view", {
        session_id: getSessionId(),
        source: "navigation",
        previous_page: "/bolsa-trabajo",
        device_category: getDeviceCategory(),
        category: "navigation",
        action: "page_view",
        page_name: "work_offers"
      });
    }
  };

  return (
    <div className={styles.mainContainer} ref={mainContainerRef}>

      {isScrolled && (!isPhone || !isCompact) && (
        <StickyNavbar
          activeTab={activeTab}
          onTabChange={handleStickyNavbarTabChange}
          logo={Logo}
        />
      )}

      <HeroSection
        logo={Logo}
        isScrolled={isScrolled}
        scrollY={scrollY}
        onTabChange={handleHeroSectionTabChange}
        activeTab={activeTab}
        totalOffers={allJobRequests?.total || 0}
        isMobile={isPhone}
        isCompact={isCompact}
      />


      <main className={`${styles.mainContent} ${isPhone && isCompact ? styles.mainContent_withCompactHero : ''}`}>
        <div className={styles.contentContainer}>
          <div 
            className={styles.tabContent} 
            ref={tabContentRef}
          >
            {activeTab === "form" ? (
              <div className={`${styles.formContainer} ${styles.fadeIn}`}>
                <WorkBagFormTyped
                  formik={formik}
                  skills={skills}
                  today={today}
                  isPhone={isPhone}
                  setShowForm={() => setActiveTab("offers")}
                />
              </div>
            ) : (
              <div className={`${styles.offersContainer} ${styles.fadeIn}`}>
                <WorkOfferGrid 
                  setShowForm={() => setActiveTab("form")} 
                />
              </div>
            )}
          </div>
        </div>
      </main>


      <div>
        {/* @ts-ignore */}
        <ReCAPTCHA 
          sitekey="6LfPa1ArAAAAAEBgDkJcb0NLb5R7Hci5Hc9adCGN" 
          size="invisible" 
          ref={recaptchaRef} 
        />
      </div>
    </div>
  );
});

const mapStateToProps = (state: RootState) => {
  return {
    skills: state.register.skills,
    allJobRequests: state.register.allJobRequests,
  };
};

export default connect(mapStateToProps, { getSkills, createWorkBagUser, openIdentityModal, getAllWorkOffers })(WorkBag);
